import { createApi } from '@reduxjs/toolkit/query/react';
import type { DirectoryType, RolesType, UsersType } from '../../types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';

export type args = {
  display_name?: DirectoryType.Name;
  username?: UsersType.Username;
  email?: DirectoryType.Email;
  password?: UsersType.Password;
  roles?: RolesType.RoleIdx[];
};

export type PatchUserArgs = Pick<args, 'display_name' | 'roles'>;

export type responses = {};

export type PatchMeArgs = Pick<UsersType.User, 'display_name' | 'email'>;

export type ChangePasswordArgs = {
  current_password: UsersType.Password;
  password: UsersType.Password;
};

export type ChangePasswordResponse = {
  detail: string;
};

export type ResetUserPasswordArgs = {
  userId: number;
  new_password: UsersType.Password;
};

export type ResetUserPasswordResponse = {
  detail: string;
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getUserMe: builder.query<UsersType.UserPartial, void>({
      query: () => ({
        url: '/user/me/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
        keepUnusedDataFor: 3600,
      }),
      providesTags: [{ type: 'User', id: 'me' }],
      // use actionExecution in /api/index.tsx for all calls
    }),

    // Get all users
    // Includes roles and permissions
    getAllUsers: builder.query<UsersType.UserPartial[], void>({
      query: () => ({
        url: '/user/users/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'User', id: 'all' }],
    }),

    // Get names of all users
    // Use to get user names
    getAllUserNames: builder.query<
      { id: number; display_name: string }[],
      void
    >({
      query: () => ({
        url: '/user/users/names/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'User', id: 'user-names' }],
    }),

    getUserById: builder.query<UsersType.UserPartial, number>({
      query: (userId) => ({
        url: `/user/${userId}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 300,
      providesTags: (result, _error, userId) =>
        result ? [{ type: 'User', id: userId }] : [],
    }),

    patchMe: builder.mutation<UsersType.UserPartial, PatchMeArgs>({
      query: (body) => ({
        url: '/user/me/',
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: [{ type: 'User', id: 'me' }],
    }),

    changePassword: builder.mutation<
      ChangePasswordResponse,
      ChangePasswordArgs
    >({
      query: (body) => ({
        url: '/user/me/password/',
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postUser: builder.mutation<UsersType.UserPartial, args>({
      query: (user) => ({
        url: '/user/register/',
        method: 'POST',
        body: user,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: ['User', { type: 'User', id: 'all' }],
    }),

    patchUser: builder.mutation<
      UsersType.UserPartial,
      { userId: number; args: PatchUserArgs }
    >({
      query: ({ userId, args }) => ({
        url: `/user/${userId}/`,
        method: 'PATCH',
        body: args,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        'User',
        { type: 'User', id: 'all' },
        { type: 'User', id: userId },
      ],
    }),

    resetUserPassword: builder.mutation<
      ResetUserPasswordResponse,
      ResetUserPasswordArgs
    >({
      query: ({ userId, new_password }) => ({
        url: `/user/${userId}/reset-password/`,
        method: 'POST',
        body: { new_password },
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    deleteUser: builder.mutation<any, number>({
      query: (userId) => ({
        url: `/user/${userId}/`,
        method: 'DELETE',
        validateStatus: (response, _result) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, userId) => [
        'User',
        { type: 'User', id: 'all' },
        { type: 'User', id: userId },
      ],
    }),
  }),
});

export const {
  useGetUserMeQuery,
  useGetAllUsersQuery,
  useGetAllUserNamesQuery,
  useGetUserByIdQuery,
  usePatchMeMutation,
  useChangePasswordMutation,
  usePostUserMutation,
  usePatchUserMutation,
  useResetUserPasswordMutation,
  useDeleteUserMutation,
} = usersApi;

export namespace UsersApi {
  export const Api = usersApi;
  export type Args = args;
  export type Responses = responses;
}
